import React, { useEffect, useRef } from 'react';
import { localeOf, useLang, useT } from '../i18n';
import { dayDate, hhmm } from '../dayplan';

export interface ProgramMoment {
  id: number;
  day: string;
  start: number;
  title: string;
  guestNote: string | null;
}

// « Le programme » du site des invités : les moments du déroulé marqués « Invités ».
// Titre collé en haut pendant qu'on le parcourt ; il monte, les traits des jours
// se tracent et les moments arrivent un à un quand la section entre à l'écran.
const GuestProgram: React.FC<{ moments: ProgramMoment[] }> = ({ moments }) => {
  const t = useT();
  const { lang } = useLang();
  const ref = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          el.classList.add('is-play');
          io.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Trait sous le titre une fois collé en haut (téléphone)
  useEffect(() => {
    const title = titleRef.current;
    if (!title) return;
    const onScroll = () => title.classList.toggle('is-stuck', title.getBoundingClientRect().top <= 0.5 && window.scrollY > 0);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const days = Array.from(new Set(moments.map((m) => m.day))).sort();
  const locale = localeOf(lang);
  const time = (min: number) => (lang === 'fr' ? hhmm(min).replace(':', 'h') : hhmm(min));
  let i = 0;

  return (
    <section className="gu-program" id="programme" ref={ref} aria-labelledby="gu-prog-title">
      <h2 id="gu-prog-title" className="gu-prog-title" ref={titleRef}>
        {t('Le programme')}
      </h2>
      <div className="gu-prog-days">
        {days.map((day) => {
          const d = dayDate(day);
          return (
            <div key={day} className="gu-prog-day">
              {days.length > 1 && (
                <div className="gu-prog-dayhead">
                  <b>{d.toLocaleDateString(locale, { weekday: 'long' })}</b>
                  <span>{d.toLocaleDateString(locale, { day: 'numeric', month: 'long' })}</span>
                </div>
              )}
              <ol className="gu-prog-list">
                {moments
                  .filter((m) => m.day === day)
                  .sort((a, b) => a.start - b.start)
                  .map((m) => (
                    <li key={m.id} style={{ ['--i' as any]: i++ }}>
                      <time>{time(m.start)}</time>
                      <span className="gu-prog-text">
                        <b>{m.title}</b>
                        {m.guestNote && <p>{m.guestNote}</p>}
                      </span>
                    </li>
                  ))}
              </ol>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default GuestProgram;
